import { useEffect, useState } from 'react';

import { apiRequest } from '@/config';
import { logError } from '@/lib/logger';

import { ConfigSelector, type ModelConfig } from '../shared/ConfigSelector';
import { Button } from '../ui/Button';
import { InputField, SelectField } from '../ui/FormField';
import { Modal } from '../ui/Modal';
import { Spinner } from '../ui/Spinner';

interface PromptSet {
  id: string;
  name: string;
  description: string | null;
  prompt_count: number;
}

interface ExecuteFromSetModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

export function ExecuteFromSetModal({
  onClose,
  onSuccess,
}: ExecuteFromSetModalProps) {
  const [promptSets, setPromptSets] = useState<PromptSet[]>([]);
  const [configs, setConfigs] = useState<ModelConfig[]>([]);
  const [selectedSetId, setSelectedSetId] = useState<string>('');
  const [selectedConfigIds, setSelectedConfigIds] = useState<string[]>([]);
  const [timeoutMinutes, setTimeoutMinutes] = useState<number>(5);
  const [parallelism, setParallelism] = useState<number>(10);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch prompt sets and configs on mount
  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        const [setsResponse, configsResponse] = await Promise.all([
          apiRequest('/api/eval/prompt-sets'),
          apiRequest('/api/eval/configs'),
        ]);

        setPromptSets(setsResponse.promptSets || []);
        const allConfigs = configsResponse.configs || [];
        setConfigs(allConfigs);

        // Pre-select enabled configs
        const enabledConfigs = allConfigs.filter(
          (c: ModelConfig) => c.enabled !== false
        );
        setSelectedConfigIds(enabledConfigs.map((c: ModelConfig) => c.id));
      } catch (err: any) {
        logError('Failed to fetch data', err);
        setError(err.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  const selectedSet = promptSets.find(s => s.id === selectedSetId);

  const handleExecute = async () => {
    if (!selectedSetId) {
      setError('Please select a prompt set');
      return;
    }
    if (selectedConfigIds.length === 0) {
      setError('Please select at least one model');
      return;
    }

    try {
      setExecuting(true);
      setError(null);

      const response = await apiRequest(
        `/api/arena/prompt-sets/${selectedSetId}/create-seed-runs`,
        {
          method: 'POST',
          body: JSON.stringify({
            config_ids: selectedConfigIds,
            timeout_ms: timeoutMinutes * 60 * 1000,
            concurrency: parallelism,
          }),
        }
      );

      if (response.success) {
        const msg =
          response.total_new_executions === 0
            ? `All ${response.run_ids.length} prompts already have results for selected models.`
            : `Processing ${response.run_ids.length} prompts: ${response.new_runs || 0} new, ${response.reused_runs || 0} existing. ${response.total_new_executions} model executions started.`;
        alert(msg);
        onSuccess();
        onClose();
      } else {
        setError(response.error || 'Execution failed');
      }
    } catch (err: any) {
      logError('Failed to execute prompt set', err);
      setError(err.message || 'Failed to start execution');
    } finally {
      setExecuting(false);
    }
  };

  const totalExecutions = selectedSet
    ? selectedSet.prompt_count * selectedConfigIds.length
    : 0;

  return (
    <Modal
      title="Execute Prompts from Set"
      subtitle="Select a prompt set and models to run"
      onClose={onClose}
      maxWidth="700px"
    >
      {loading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            padding: '3rem',
          }}
        >
          <Spinner />
        </div>
      ) : (
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}
        >
          {error && (
            <div
              style={{
                padding: '0.75rem 1rem',
                background: '#FEF2F2',
                border: '1px solid #FECACA',
                borderRadius: '8px',
                color: '#DC2626',
                fontSize: '0.875rem',
              }}
            >
              {error}
            </div>
          )}

          {/* Prompt Set Selection */}
          <SelectField
            label="Prompt Set"
            value={selectedSetId}
            onChange={e => setSelectedSetId(e.target.value)}
            options={promptSets.map(set => ({
              value: set.id,
              label: `${set.name} (${set.prompt_count} prompts)`,
            }))}
            placeholder="Select a prompt set..."
            required
          />

          {/* Selected Set Info */}
          {selectedSet && (
            <div
              style={{
                padding: '1rem',
                background: '#F5F0EB',
                borderRadius: '8px',
                border: '1px solid #E8E0D8',
              }}
            >
              <div
                style={{
                  fontWeight: 600,
                  fontSize: '0.9375rem',
                  color: '#1A1A1A',
                  marginBottom: '0.25rem',
                }}
              >
                {selectedSet.name}
              </div>
              {selectedSet.description && (
                <div
                  style={{
                    fontSize: '0.8125rem',
                    color: '#6B6B6B',
                    marginBottom: '0.5rem',
                  }}
                >
                  {selectedSet.description}
                </div>
              )}
              <div style={{ fontSize: '0.8125rem', color: '#2D2D2D' }}>
                <strong>{selectedSet.prompt_count}</strong> prompts will be
                executed with <strong>{selectedConfigIds.length}</strong> models
                = <strong>{totalExecutions}</strong> total executions
              </div>
            </div>
          )}

          {/* Model Config Selection */}
          <ConfigSelector
            configs={configs}
            selectedConfigIds={selectedConfigIds}
            onSelectionChange={setSelectedConfigIds}
            defaultFilter="enabled"
          />

          {/* Execution Settings */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '1rem',
            }}
          >
            <InputField
              label="Timeout (minutes)"
              type="number"
              min={1}
              max={30}
              value={timeoutMinutes}
              onChange={e => setTimeoutMinutes(Number(e.target.value))}
              helperText="Max time per model execution"
            />
            <InputField
              label="Parallelism"
              type="number"
              min={1}
              max={50}
              value={parallelism}
              onChange={e => setParallelism(Number(e.target.value))}
              helperText="Concurrent model executions"
            />
          </div>

          {/* Actions */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '0.75rem',
              paddingTop: '0.5rem',
              borderTop: '1px solid #E8E0D8',
            }}
          >
            <Button variant="secondary" onClick={onClose} disabled={executing}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleExecute}
              disabled={
                executing || !selectedSetId || selectedConfigIds.length === 0
              }
              loading={executing}
            >
              Execute {totalExecutions > 0 ? `${totalExecutions} Tasks` : ''}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
